import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Search } from "lucide-react";
import { resolveImageUrl } from "@/lib/images";

export default function BlogIndexPage() {
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState(null);
  const q = params.get("q") || "";
  const tag = params.get("tag") || "";
  const page = parseInt(params.get("page") || "1", 10);

  useEffect(() => {
    api.get("/blog", { params: { q: q || undefined, tag: tag || undefined, page, page_size: 12 } })
      .then((r) => setData(r.data));
  }, [q, tag, page]);

  return (
    <div className="container-page py-12" data-testid="blog-page">
      <span className="chip">Blog</span>
      <h1 className="mt-3 font-display text-4xl font-bold text-primary lg:text-5xl">Stories from our community</h1>
      <p className="mt-2 text-foreground/70">Wellness, advocacy, and updates from Black-led practices and organizations.</p>

      <div className="card-surface mt-8 flex flex-col gap-3 p-3 sm:flex-row" data-testid="blog-search">
        <label className="flex w-full items-center gap-2 rounded-md bg-sand/60 px-3 py-2.5">
          <Search className="h-4 w-4 text-muted-foreground" />
          <input
            defaultValue={q}
            placeholder="Search posts…"
            className="w-full bg-transparent outline-none"
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                const next = new URLSearchParams(params);
                if (e.target.value) next.set("q", e.target.value); else next.delete("q");
                next.delete("page");
                setParams(next, { replace: true });
              }
            }}
            data-testid="blog-q"
          />
        </label>
        {tag && (
          <button
            onClick={() => {
              const next = new URLSearchParams(params);
              next.delete("tag");
              setParams(next, { replace: true });
            }}
            className="rounded-md bg-primary/8 px-3 py-1 text-sm font-semibold text-primary"
          >
            tag: {tag} ✕
          </button>
        )}
      </div>

      {!data ? (
        <p className="mt-10 text-muted-foreground">Loading…</p>
      ) : data.results.length === 0 ? (
        <div className="card-surface mt-8 p-12 text-center" data-testid="blog-empty">
          <h3 className="font-display text-2xl font-bold text-primary">No posts yet</h3>
          <p className="mt-2 text-foreground/70">Be the first to share — claim your listing and start writing.</p>
        </div>
      ) : (
        <div className="mt-10 grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
          {data.results.map((p) => (
            <Link to={`/blog/${p.slug}`} key={p.id} className="card-surface card-hover overflow-hidden" data-testid={`blog-card-${p.id}`}>
              <div className="aspect-[16/10] overflow-hidden bg-muted">
                {p.hero_url ? (
                  <img src={resolveImageUrl(p.hero_url)} alt={p.title} className="h-full w-full object-cover" />
                ) : (
                  <div className="grid h-full place-items-center text-muted-foreground">No image</div>
                )}
              </div>
              <div className="space-y-2 p-5">
                {p.listing?.category ? (
                  <span className="chip">{p.listing.category}</span>
                ) : p.tags?.length > 0 && (
                  <span className="chip">{p.tags[0]}</span>
                )}
                <h3 className="font-display text-2xl font-bold leading-tight text-primary line-clamp-2">{p.title}</h3>
                {(p.author_byline || p.listing?.practice_name) && (
                  <p className="text-sm text-muted-foreground line-clamp-2">By {p.author_byline || p.listing.practice_name}</p>
                )}
                <p className="text-xs text-muted-foreground">{new Date(p.published_at).toLocaleDateString()}</p>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
